import React, { useState } from 'react';
import Icon from '@mdi/react';
import { mdiAlertCircle, mdiCreditCardRefund, mdiCreditCardPlus } from '@mdi/js';
import CustomFormInput from './forms/CustomFormInput';
import { formatCurrencyFromMinor } from '../utils/currency';
import './ReturnActionPanel.css';

const ReturnActionPanel = ({ booking, onConfirm, onSkip, isSubmitting, isGuest, variant = 'inline' }) => {
    const [selectedType, setSelectedType] = useState(null);
    // Optional note to the student (mirrors the cancel flow). Posted to the booking thread.
    const [message, setMessage] = useState('');

    // Server-computed display pricing (effectivePricing) — single source of truth.
    // Never compute booking prices client-side.
    const pricing = booking?.pricing || {};
    const ep = booking?.effectivePricing || {};
    const currency = (ep.currency || pricing.currency || 'AUD').toUpperCase();
    const lessonFee = ep.lessonFee || 0;
    const venueHire = ep.venueFee || 0;
    const platformFee = pricing.platformFeeSubtotal || 0;
    const stripeFee = pricing.processingFeeSubtotal || 0;
    // The refundable/creditable cap is the PRINCIPAL (lesson + venue). The service fee
    // (platform + Stripe) is the platform's cut and is never refundable.
    const principal = ep.principal || 0;
    // How the booking was paid decides which return routes exist:
    //   stripe                         → refund to card OR credit
    //   external (cash/eftpos/bank/other) → record refund (money returned offline) OR credit
    //   platform_credit                → nothing to return; cancelling re-grants the credit automatically
    const paidBy = ep.paidBy || null;
    const isExternalPaid = ['bank_transfer', 'eftpos', 'cash', 'other'].includes(paidBy);
    const isCreditPaid = paidBy === 'platform_credit';

    // Returns accumulate: only what REMAINS (principal minus prior partials) is refundable now.
    // The API serializes the cumulative returned amount as booking.returnAmount in DOLLARS
    // (returnAmountCents / 100) — convert back to cents here. (Reading returnAmountCents off the
    // serialized booking was the bug that let a 2nd refund offer the full amount + credit again.)
    const previousReturnedCents = Math.round((booking?.returnAmount || 0) * 100);
    const remaining = Math.max(0, principal - previousReturnedCents);

    // Editable amount (DOLLARS — this is a $ input; arrows step by $1, cents can be typed). ep.* are integer
    // cents, so divide by 100 for the dollar-denominated input/cap. Defaults to the
    // lesson fee (capped to remaining); venue hire is opt-in.
    const [includeVenue, setIncludeVenue] = useState(false);
    // Held as a raw STRING so the field can be cleared/edited freely. A number state +
    // parseFloat-on-change snaps an emptied field back to 0 — the "leading zero you can't
    // delete" bug. Parsed to a number for the calculations below (mirrors AdminPaymentModal).
    const [amountStr, setAmountStr] = useState(String(Math.min(lessonFee, remaining) / 100));
    // Inline confirm step shown when the amount covers the full remaining (which CANCELS the booking).
    const [confirmingFull, setConfirmingFull] = useState(false);

    const amountNum = parseFloat(amountStr);
    // Max refundable depends on the venue toggle: the lesson fee only by default, or the full
    // principal (lesson + venue) when venue hire is included — both capped to what remains after
    // any prior partial returns.
    const maxRefundableCents = Math.min(includeVenue ? principal : lessonFee, remaining);
    const clampedAmount = Math.min(Math.max(0, Number.isFinite(amountNum) ? amountNum : 0), maxRefundableCents / 100);
    const amountCents = Math.round(clampedAmount * 100);
    // Typed amount is over the max. We show a "enter an amount up to $X" prompt and DISABLE the
    // confirm button (rather than silently clamping) so the user corrects it themselves.
    const exceedsMax = Number.isFinite(amountNum) && amountNum > maxRefundableCents / 100 + 0.001;
    // CREDIT is whole-lesson only (we don't do partial credits) — it always returns the full
    // remaining and cancels the booking. Only a REFUND uses the editable partial amount.
    const isCreditReturn = selectedType === 'credit';
    const effectiveAmountCents = isCreditReturn ? remaining : amountCents;
    // A full return (cumulatively) cancels the booking + removes it from the calendar; a partial keeps it.
    const isFullReturn = effectiveAmountCents >= remaining && effectiveAmountCents > 0;

    const skipBtnClass = variant === 'base-modal' ? 'base-modal-cancel-btn' : 'btn-turquoise';
    const confirmBtnClass = variant === 'base-modal' ? 'base-modal-save-btn' : 'btn-purple';

    const handleVenueToggle = checked => {
        setIncludeVenue(checked);
        setAmountStr(String(Math.min(checked ? principal : lessonFee, remaining) / 100));
    };

    const handleConfirm = () => {
        if (!selectedType || effectiveAmountCents <= 0) return;
        // Full return is destructive (cancels the booking) — gate it behind an inline warning.
        if (isFullReturn && !confirmingFull) { setConfirmingFull(true); return; }
        onConfirm(selectedType, effectiveAmountCents, message.trim() || undefined);
    };

    return (
        <div className="return-action-panel">
            <div className="return-action-header">
                <Icon
                    path={mdiAlertCircle}
                    size={1.1}
                    color="var(--dl-color-needs-action)"
                    style={{ flexShrink: 0 }}
                />
                <span className="return-action-header-title">Issue a Refund?</span>
            </div>

            <div className="return-action-body">
                {confirmingFull ? (
                    <p className="return-action-method-notice">
                        {isCreditReturn
                            ? <>This credits the full lesson ({formatCurrencyFromMinor(effectiveAmountCents, currency)}) to the student&apos;s account and <strong>cancels this booking, removing it from your calendar</strong>. Continue?</>
                            : <>This returns the full remaining {formatCurrencyFromMinor(effectiveAmountCents, currency)} and will <strong>cancel this booking, removing it from your calendar</strong>. Continue?</>}
                    </p>
                ) : isCreditPaid ? (
                    <p className="return-action-guest-notice">
                        This booking was paid with account credit. Cancelling it returns the credit
                        to the student automatically. There is nothing to refund to a card.
                    </p>
                ) : (
                    <>
                        {isExternalPaid && (
                            <p className="return-action-method-notice">
                                Paid by {paidBy === 'eftpos' ? 'EFTPOS' : paidBy === 'cash' ? 'cash' : paidBy === 'bank_transfer' ? 'bank transfer' : 'another method'}. No refund is processed through the platform.
                                Record refund to reconcile your account{isGuest ? '.' : ', or issue credit.'}
                            </p>
                        )}
                        {/* Breakdown — lesson + venue are refundable; the service fee is not.
                            Fee rows only apply to card payments (no fee was collected on an
                            external payment). */}
                        <div className="return-action-breakdown">
                            <div className="return-action-bd-row">
                                <span>Lesson fee</span>
                                <span>{formatCurrencyFromMinor(lessonFee, currency)}</span>
                            </div>
                            {venueHire > 0 && (
                                <div className="return-action-bd-row">
                                    <span>Venue hire</span>
                                    <span>{formatCurrencyFromMinor(venueHire, currency)}</span>
                                </div>
                            )}
                            {!isExternalPaid && (
                                <>
                                    <div className="return-action-bd-row return-action-bd-muted">
                                        <span>Platform fee</span>
                                        <span>{formatCurrencyFromMinor(platformFee, currency)}</span>
                                    </div>
                                    <div className="return-action-bd-row return-action-bd-muted">
                                        <span>Stripe fee (non-refundable)</span>
                                        <span>{formatCurrencyFromMinor(stripeFee, currency)}</span>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Amount + venue opt-in are REFUND-only (refunds can be partial). Credit is
                            whole-lesson, so it shows a fixed note instead of an editable amount. */}
                        {isCreditReturn ? (
                            <p className="return-action-amount-hint">
                                Credits the full lesson ({formatCurrencyFromMinor(remaining, currency)}) to the student&apos;s account and cancels the booking.
                            </p>
                        ) : (
                            <>
                                {venueHire > 0 && (
                                    <div className="return-action-venue-optin">
                                        <CustomFormInput
                                            type="checkbox"
                                            variant="turquoise"
                                            name="include-venue"
                                            checked={includeVenue}
                                            onChange={e => handleVenueToggle(e.target.checked)}
                                            disabled={isSubmitting}
                                            label=""
                                        />
                                        <span>Include venue hire ({formatCurrencyFromMinor(venueHire, currency)})</span>
                                    </div>
                                )}

                                <div className="return-action-amount-field">
                                    <label htmlFor="return-amount">Amount to refund</label>
                                    <input
                                        id="return-amount"
                                        type="number"
                                        min={0}
                                        max={maxRefundableCents / 100}
                                        step="1"
                                        value={amountStr}
                                        onChange={e => setAmountStr(e.target.value)}
                                        disabled={isSubmitting}
                                        className={`return-action-amount-input${exceedsMax ? ' return-action-amount-input-error' : ''}`}
                                    />
                                    {exceedsMax && (
                                        <p className="return-action-amount-error">
                                            Enter an amount up to {formatCurrencyFromMinor(maxRefundableCents, currency)}{includeVenue ? '' : ' (or tick "Include venue hire" to refund more)'}.
                                        </p>
                                    )}
                                    <p className="return-action-amount-hint">
                                        Up to {formatCurrencyFromMinor(maxRefundableCents, currency)}{previousReturnedCents > 0 ? ' remaining' : (includeVenue ? ' (lesson + venue)' : ' (lesson fee)')}. The service fee is not refundable. A partial amount keeps the booking; the full amount cancels it.
                                    </p>
                                </div>
                            </>
                        )}

                        <div className="return-action-options">
                            {/* Refund option is available for BOTH card and external (bank
                                transfer) bookings. Card → Stripe reversal; external → recorded
                                only (the studio returns the money offline, the platform records
                                it). Backend issueReturn skips Stripe for externally-paid. */}
                            <div
                                className={`return-action-option${selectedType === 'refund' ? ' selected' : ''}`}
                                onClick={() => !isSubmitting && setSelectedType('refund')}
                            >
                                <CustomFormInput
                                    type="radio"
                                    variant="turquoise"
                                    name="return-type"
                                    value="refund"
                                    checked={selectedType === 'refund'}
                                    onChange={() => setSelectedType('refund')}
                                    disabled={isSubmitting}
                                    label=""
                                />
                                <Icon path={mdiCreditCardRefund} size={0.85} color="var(--dl-color-purple-default)" />
                                <span>{isExternalPaid ? 'Record refund' : 'Refund to card'}</span>
                            </div>
                            {/* Credit is whole-lesson: an account-holder privilege (not for guests)
                                and not offered once a partial refund has been taken on this booking. */}
                            {!isGuest && previousReturnedCents === 0 && (
                                <div
                                    className={`return-action-option${selectedType === 'credit' ? ' selected' : ''}`}
                                    onClick={() => !isSubmitting && setSelectedType('credit')}
                                >
                                    <CustomFormInput
                                        type="radio"
                                        variant="turquoise"
                                        name="return-type"
                                        value="credit"
                                        checked={selectedType === 'credit'}
                                        onChange={() => setSelectedType('credit')}
                                        disabled={isSubmitting}
                                        label=""
                                    />
                                    <Icon path={mdiCreditCardPlus} size={0.85} color="var(--dl-color-purple-default)" />
                                    <span>Credit to account</span>
                                </div>
                            )}
                        </div>

                        {/* Optional note to the student (mirrors the cancel flow). Posted to the
                            booking thread — as the cancellation message when the booking cancels. */}
                        {selectedType && (
                            <div className="return-action-amount-field">
                                <label htmlFor="return-message">Message to the student (optional)</label>
                                <textarea
                                    id="return-message"
                                    className="return-action-amount-input"
                                    rows={2}
                                    maxLength={500}
                                    placeholder="Add a note the student will see, e.g. why you're refunding."
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    disabled={isSubmitting}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="return-action-footer">
                {confirmingFull ? (
                    <>
                        <button className={skipBtnClass} onClick={() => setConfirmingFull(false)} disabled={isSubmitting}>
                            Back
                        </button>
                        <button className={confirmBtnClass} onClick={() => onConfirm(selectedType, effectiveAmountCents, message.trim() || undefined)} disabled={isSubmitting}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Icon path={mdiCreditCardRefund} size={0.8} />
                                {isSubmitting ? 'Processing...' : (selectedType === 'credit' ? 'Yes, credit and cancel' : 'Yes, refund and cancel')}
                            </span>
                        </button>
                    </>
                ) : isCreditPaid ? (
                    <button
                        className="btn-gray"
                        onClick={onSkip}
                        disabled={isSubmitting}
                    >
                        Close
                    </button>
                ) : (
                    <>
                        <button
                            className={skipBtnClass}
                            onClick={onSkip}
                            disabled={isSubmitting}
                        >
                            {isExternalPaid ? 'Do not record refund' : 'Handle offline'}
                        </button>
                        <button
                            className={confirmBtnClass}
                            onClick={handleConfirm}
                            disabled={!selectedType || effectiveAmountCents <= 0 || (!isCreditReturn && exceedsMax) || isSubmitting}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Icon path={mdiCreditCardRefund} size={0.8} />
                                {isSubmitting ? 'Processing...'
                                    : !selectedType ? 'Confirm'
                                    : selectedType === 'credit' ? `Credit ${formatCurrencyFromMinor(effectiveAmountCents, currency)}`
                                    : isExternalPaid ? `Record refund ${formatCurrencyFromMinor(effectiveAmountCents, currency)}`
                                    : `Refund ${formatCurrencyFromMinor(effectiveAmountCents, currency)}`}
                            </span>
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default ReturnActionPanel;
